import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, HelpCircle } from 'lucide-react';
import { FAQ_VIDEOS } from '@/lib/faqVideos';

/**
 * FaqCarousel — single-row, INFINITELY looping row of FAQ videos.
 *  • The list is rendered twice; auto-scroll advances one card every ~3.5s
 *    and wraps seamlessly (after the last card the first re-appears — the
 *    scroll never jumps visibly back to the start).
 *  • Videos are PAUSED until the user taps play; they play IN PLACE
 *    (no fullscreen). Auto-rotation freezes while a video is playing and
 *    resumes on pause/ended.
 */
const ROTATE_MS = 3500;

// Two copies enable a seamless infinite marquee.
const SLIDES = [...FAQ_VIDEOS, ...FAQ_VIDEOS].map((v, i) => ({
  ...v,
  uid: `${v.id}-${i < FAQ_VIDEOS.length ? 'a' : 'b'}`,
}));

export default function FaqCarousel() {
  const scrollRef = useRef(null);
  const videoRefs = useRef({});
  const [playingUid, setPlayingUid] = useState(null);

  const isPaused = playingUid !== null;

  // Continuous auto-rotation with seamless wrap-around.
  useEffect(() => {
    if (isPaused) return undefined;
    const el = scrollRef.current;
    if (!el) return undefined;
    const timer = setInterval(() => {
      if (!el) return;
      const card = el.querySelector('[data-faq-card]');
      const step = card ? card.offsetWidth + 16 : 220; // 16 = gap-4
      const firstCopyWidth = el.scrollWidth / 2;
      // Invisible wrap: identical copies make this jump imperceptible.
      if (el.scrollLeft >= firstCopyWidth) {
        el.scrollLeft -= firstCopyWidth;
      }
      el.scrollBy({ left: step, behavior: 'smooth' });
    }, ROTATE_MS);
    return () => clearInterval(timer);
  }, [isPaused]);

  const handlePlay = useCallback((uid) => {
    Object.entries(videoRefs.current).forEach(([key, node]) => {
      if (key !== uid && node && !node.paused) node.pause();
    });
    const node = videoRefs.current[uid];
    if (node) {
      node.play().catch(() => {});
      setPlayingUid(uid);
    }
  }, []);

  const handleStop = useCallback((uid) => {
    setPlayingUid((cur) => (cur === uid ? null : cur));
  }, []);

  return (
    <div className="pb-4" data-testid="faq-carousel">
      <div className="flex items-center justify-between px-6 md:px-8 mb-4">
        <div>
          <h3 className="text-sm font-bold text-black uppercase tracking-wider flex items-center gap-1.5">
            <HelpCircle className="w-4 h-4 text-[#fbb040]" /> FAQ
          </h3>
          <p className="text-xs text-gray-400 mt-0.5">Preguntas frecuentes</p>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex gap-4 overflow-x-auto no-scrollbar px-6 md:px-8"
        data-testid="faq-carousel-row"
      >
        {SLIDES.map((v) => {
          const isThisPlaying = playingUid === v.uid;
          return (
            <div
              key={v.uid}
              data-faq-card
              className="flex-shrink-0 w-40 sm:w-44"
              data-testid={`faq-card-${v.uid}`}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-sm aspect-[9/16] bg-black">
                <video
                  ref={(el) => { videoRefs.current[v.uid] = el; }}
                  src={v.url}
                  preload="metadata"
                  playsInline
                  controls={isThisPlaying}
                  className="absolute inset-0 w-full h-full object-cover"
                  onPlay={() => setPlayingUid(v.uid)}
                  onPause={() => handleStop(v.uid)}
                  onEnded={() => handleStop(v.uid)}
                  data-testid={`faq-video-${v.uid}`}
                />

                {/* Overlay (question + play) — hidden while playing */}
                {!isThisPlaying && (
                  <button
                    type="button"
                    onClick={() => handlePlay(v.uid)}
                    className="absolute inset-0 flex flex-col justify-end p-3 text-left"
                    aria-label={`Reproducir: ${v.question}`}
                    data-testid={`faq-play-${v.uid}`}
                  >
                    {/* Soft aesthetic cover gradient: #fbb040 (bottom) → transparent (top) */}
                    <div className="absolute inset-0 bg-gradient-to-t from-[#fbb040] via-[#fbb040]/35 to-transparent" />
                    <div className="relative z-10 flex flex-col gap-2">
                      <span className="w-11 h-11 rounded-full bg-white/95 flex items-center justify-center shadow-lg self-start">
                        <Play className="w-5 h-5 text-[#1f2d5a] ml-0.5" fill="currentColor" />
                      </span>
                      <p className="text-white font-bold text-sm leading-snug drop-shadow-md">
                        {v.question}
                      </p>
                    </div>
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
